import React, { useEffect } from 'react';
import { Button, Modal } from 'common/lazy';
import { useClassName, useWindowSize } from 'common/hooks';
import { SVGIcon as Icon } from 'common/components';
import './compare-tickets.less';
import { Swiper, SwiperSlide } from 'swiper/react';
import { usePagination } from 'public/screens/Home/Components/usePagination';
import { Pagination } from 'swiper';

const CompareTickets = ({ ticketsCards, open, onClose, onOpen, trigger }) => {
  const className = useClassName('CompareTickets');
  // `trigger` is optional: pass `null` to control the modal externally (e.g.
  // open it from another component) instead of using the default button.
  const modalTrigger =
    trigger === undefined ? (
      <Button
        primary
        content="Compare Tickets"
        className={className('button')}
        onClick={onOpen}
      />
    ) : (
      trigger
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={className('modal')}
      trigger={modalTrigger}
      content={
        <CompareTicketsContent ticketsCards={ticketsCards} onClose={onClose} />
      }
    />
  );
};

const CompareTicketsContent = ({ ticketsCards, onClose }) => {
  const className = useClassName('CompareTicketsContent');
  const pagination = usePagination({});
  const { isMobile } = useWindowSize();

  const handleSwiperInit = (swiper) => {
    setTimeout(normalizeCellHeights, 50);

    // Force pagination render if needed
    if (isMobile && swiper.pagination) {
      setTimeout(() => {
        swiper.pagination.render();
        swiper.pagination.update();
      }, 100);
    }
  };

  const handleSlideChange = () => {
    setTimeout(normalizeCellHeights, 10);
  };

  useEffect(() => {
    window.addEventListener('resize', normalizeCellHeights);
    return () => window.removeEventListener('resize', normalizeCellHeights);
  }, []);

  const slides = [
    {
      title: '',
      visits: 'Visits',
      parksAndRidesIncluded: 'Parks and Attractions Included',
      validUpTo: 'Valid Through',
      price: 'Price',
    },
    ...ticketsCards.slides.map((slide) => {
      return {
        title: slide.name,
        visits: slide.visits,
        featured: slide.featured,
        useStartingAt: slide?.useStartingAt || false,
        parksAndRidesIncluded: slide.parkScreens?.join(', '),
        validUpTo: slide.validUpTo,
        price: slide.price,
        link: slide.link,
      };
    }),
  ];
  console.log({ slides: ticketsCards.slides });

  const normalizeCellHeights = () => {
    const slides = document.querySelectorAll('.slide-content__content');

    if (!slides.length) return;

    const cellsPerSlide = [
      ...slides[0].querySelectorAll('.slide-content__cell'),
    ].length;

    slides.forEach((slide) => {
      slide.querySelectorAll('.slide-content__cell').forEach((cell) => {
        cell.style.height = 'auto';
      });
    });

    for (let i = 0; i < cellsPerSlide; i++) {
      let maxHeight = 0;

      slides.forEach((slide) => {
        const cell = slide.querySelectorAll('.slide-content__cell')[i];
        if (!cell) return;
        const h = cell.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      slides.forEach((slide) => {
        const cell = slide.querySelectorAll('.slide-content__cell')[i];
        if (cell) cell.style.height = `${maxHeight}px`;
      });
    }
  };

  return (
    <div className={className('container')}>
      <h1 className={className('title')}>
        <span>Compare Tickets</span>
        <div className={className('title__close')} onClick={onClose}>
          <Icon name="close" width={8} height={8} />
        </div>
      </h1>
      <div className={className('inner')}>
        <Swiper
          {...(isMobile ? { ...pagination } : {})}
          modules={[Pagination]}
          centeredSlides={false}
          spaceBetween={0}
          breakpoints={{
            0: {
              slidesPerView: 2,
            },
            768: {
              slidesPerView: 3,
            },
            1128: {
              slidesPerView: 6,
            },
          }}
          speed={500}
          onSwiper={handleSwiperInit}
          onAfterInit={(swiper) => {
            if (isMobile && swiper.pagination) {
              swiper.pagination.render();
              swiper.pagination.update();
            }
          }}
          preventClicksPropagation={true}
          className={className('carousel')}
          onSlideChange={handleSlideChange}
          draggable={false}>
          {slides.slice(isMobile ? 1 : 0).map((slide, index) => {
            return (
              <SwiperSlide
                key={slide.name}
                className={className(
                  'carousel__slide',
                  slide.featured ? 'featured' : ''
                )}>
                <SlideContent
                  slide={slide}
                  firstColumn={index === 0}
                  lastColumn={index === slides.length - 1}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

const SlideContent = ({ slide, firstColumn, lastColumn }) => {
  const className = useClassName('SlideContent');

  return (
    <div className={className('content', slide.featured ? 'featured' : '')}>
      {slide.featured && (
        <div className={className('content__featured-badge')}>
          <span>{slide.featured}</span>
          <Icon
            className={className('content__featured-badge__icon')}
            name="stars"
            width={24}
            height={24}
          />
        </div>
      )}
      <div
        className={className(
          'cell slide-content__cell--first-row',
          firstColumn ? 'first-column' : lastColumn ? 'last-column' : ''
        )}>
        {slide.title}
      </div>
      <div
        className={className(
          'cell',
          firstColumn ? 'first-column' : lastColumn ? 'last-column' : ''
        )}>
        {slide.visits}
      </div>
      <div
        className={className(
          'cell',
          firstColumn ? 'first-column' : lastColumn ? 'last-column' : ''
        )}>
        {slide.parksAndRidesIncluded}
      </div>
      <div
        className={className(
          'cell',
          firstColumn ? 'first-column' : lastColumn ? 'last-column' : ''
        )}>
        {slide.validUpTo}
      </div>
      <div
        className={className(
          'cell',
          firstColumn ? 'first-column' : lastColumn ? 'last-column' : ''
        )}>
        <span className={className('cell__text')}>
          {slide.useStartingAt && 'Starting at '}
        </span>
        {!slide.title ? '' : ''}
        {slide.price}
      </div>
      <div
        className={className(
          'cell',
          firstColumn ? 'first-column' : lastColumn ? 'last-column' : ''
        )}>
        {!slide.title ? null : (
          <Button
            primary
            href={slide.link?.url}
            className={className('button', slide.featured ? 'featured' : '')}
            content={slide.link?.label}
            target={slide.link?.type === 'external' ? '_blank' : '_self'}
          />
        )}
      </div>
    </div>
  );
};

export default CompareTickets;
